import { TileDef, MaterialType } from '../types';

export interface Animation {
  type: 'dig' | 'discovery' | 'scan' | 'charges';
  x: number;
  y: number;
  startTime: number;
  duration: number;
  data?: any;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  alpha: number;
}

const MATERIAL_COLORS: Record<MaterialType, string> = {
  topsoil: '#5D4037',
  clay: '#A0522D',
  sediment: '#C19A6B',
  rock: '#808080',
  bedrock: '#4A4A4A',
  sand: '#E8D5B5',
  permafrost: '#B0C4DE',
  water: '#2A6B8A',
  volcanic: '#2D1B2E',
};

const TEAL_ACCENT = '#2A9D8F';
const DARK_CHARCOAL = '#1A1A2E';
const AGED_GOLD = '#B8860B';
const TERRACOTTA = '#C75B39';
const DEEP_BROWN = '#3E2723';

const TOOL_ICONS: Record<string, string> = {
  pickaxe: '\u26CF\uFE0F',
  brush: '\uD83D\uDD8C\uFE0F',
  trowel: '\uD83D\uDD27',
  gpr: '\uD83D\uDCE1',
  charges: '\uD83D\uDCA5',
};

export class Renderer {
  canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private tileSize: number = 48;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private gridSize: number = 12;
  private grid: TileDef[][] = [];
  private animations: Animation[] = [];
  private hoveredTile: { x: number; y: number } | null = null;
  private selectedTool: string = 'trowel';
  private scrollX: number = 0;
  private scrollY: number = 0;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private scrollStartX: number = 0;
  private scrollStartY: number = 0;
  private animFrameId: number = 0;
  private pulsePhase: number = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get 2D rendering context');
    }
    this.ctx = ctx;
    this.resize();

    // Watch for parent size changes (fixes zero-dimension canvas after screen transitions)
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => this.resize());
      // Observe once canvas is in the DOM with a parent
      const checkParent = () => {
        if (this.canvas.parentElement) {
          ro.observe(this.canvas.parentElement);
        } else {
          requestAnimationFrame(checkParent);
        }
      };
      checkParent();
    }
  }

  setGrid(grid: TileDef[][], gridSize: number): void {
    this.grid = grid;
    this.gridSize = gridSize;
    // Only resize if the canvas parent has non-zero dimensions.
    // Avoids zeroing out canvas during mid-transition layout changes.
    const parent = this.canvas.parentElement;
    if (parent && parent.clientWidth > 0 && parent.clientHeight > 0) {
      this.resize();
    }
  }

  render(grid: TileDef[][]): void {
    this.grid = grid;
    this.pulsePhase = (performance.now() / 1000) * Math.PI * 2;

    const { ctx, canvas } = this;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Fill background
    ctx.fillStyle = DARK_CHARCOAL;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate offset to center the grid
    const totalGridW = this.gridSize * this.tileSize;
    const totalGridH = this.gridSize * this.tileSize;
    this.offsetX = Math.floor((canvas.width - totalGridW) / 2) + this.scrollX;
    this.offsetY = Math.floor((canvas.height - totalGridH) / 2) + this.scrollY;

    // Draw tiles
    for (let gy = 0; gy < this.gridSize; gy++) {
      for (let gx = 0; gx < this.gridSize; gx++) {
        if (grid[gy] && grid[gy][gx]) {
          this.drawTile(grid[gy][gx], gx, gy);
        }
      }
    }

    this.drawGridLines();
    this.drawHover();
    this.drawToolCursor();
    this.updateAnimations();
    this.drawAnimations();
  }

  private drawTile(tile: TileDef, x: number, y: number): void {
    const px = this.offsetX + x * this.tileSize;
    const py = this.offsetY + y * this.tileSize;
    const size = this.tileSize;
    const { ctx } = this;

    // Check if tile is visible on screen
    if (px + size < 0 || py + size < 0 || px > this.canvas.width || py > this.canvas.height) {
      return;
    }

    const isFullyExcavated = tile.currentLayer >= tile.layers.length;

    if (isFullyExcavated) {
      // Fully dug out - very dark pit
      ctx.fillStyle = '#0D0D15';
      ctx.fillRect(px, py, size, size);

      // Subtle inner shadow
      const grad = ctx.createRadialGradient(
        px + size / 2, py + size / 2, size * 0.1,
        px + size / 2, py + size / 2, size * 0.6
      );
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, 'rgba(0,0,0,0.4)');
      ctx.fillStyle = grad;
      ctx.fillRect(px, py, size, size);
    } else {
      // Get current layer material color
      const layer = tile.layers[tile.currentLayer];
      const baseColor = this.getMaterialColor(layer.material);

      // Darken based on depth - deeper layers are darker
      const depthDarken = tile.currentLayer * 0.06;
      const color = this.darkenColor(baseColor, depthDarken);

      ctx.fillStyle = color;
      ctx.fillRect(px, py, size, size);

      // Add subtle texture noise
      this.drawTileTexture(tile, px, py);

      // Depth indicator
      this.drawDepthIndicator(tile, px, py);
    }

    // Overlays
    if (tile.scanned) {
      this.drawScannedOverlay(tile, px, py);
    }

    if (tile.marked) {
      this.drawMarkedOverlay(px, py);
    }

    // Show artifact icon only after the artifact layer has been excavated past
    if (tile.revealed && tile.artifactId && tile.currentLayer > tile.artifactLayer) {
      this.drawArtifactIcon(tile, px, py);
    }
    // Show a subtle hint shimmer when approaching artifact layer (within 1 layer)
    else if (tile.revealed && tile.artifactId && tile.artifactLayer - tile.currentLayer <= 1 && tile.currentLayer < tile.layers.length) {
      this.drawArtifactHint(px, py);
    }
  }

  private drawTileTexture(tile: TileDef, px: number, py: number): void {
    const { ctx } = this;
    const size = this.tileSize;

    // Simple deterministic noise based on position
    const seed = (px * 7 + py * 13) & 0xFF;
    ctx.fillStyle = `rgba(0,0,0,${0.03 + (seed % 5) * 0.01})`;
    for (let i = 0; i < 3; i++) {
      const nx = px + ((seed + i * 37) % size);
      const ny = py + ((seed + i * 53) % size);
      const ns = 2 + (seed + i) % 3;
      ctx.fillRect(nx, ny, ns, ns);
    }

    // Light speckle on top
    ctx.fillStyle = `rgba(255,255,255,${0.02 + (seed % 3) * 0.01})`;
    for (let i = 0; i < 2; i++) {
      const nx = px + ((seed + i * 23) % size);
      const ny = py + ((seed + i * 41) % size);
      ctx.fillRect(nx, ny, 1, 1);
    }
  }

  private drawGridLines(): void {
    const { ctx } = this;
    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 1;

    const totalW = this.gridSize * this.tileSize;
    const totalH = this.gridSize * this.tileSize;

    // Vertical lines
    for (let x = 0; x <= this.gridSize; x++) {
      const px = this.offsetX + x * this.tileSize;
      ctx.beginPath();
      ctx.moveTo(px + 0.5, this.offsetY);
      ctx.lineTo(px + 0.5, this.offsetY + totalH);
      ctx.stroke();
    }

    // Horizontal lines
    for (let y = 0; y <= this.gridSize; y++) {
      const py = this.offsetY + y * this.tileSize;
      ctx.beginPath();
      ctx.moveTo(this.offsetX, py + 0.5);
      ctx.lineTo(this.offsetX + totalW, py + 0.5);
      ctx.stroke();
    }

    // Outer border - slightly brighter
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 2;
    ctx.strokeRect(this.offsetX, this.offsetY, totalW, totalH);
  }

  private drawHover(): void {
    if (!this.hoveredTile) return;

    const { x, y } = this.hoveredTile;
    if (x < 0 || x >= this.gridSize || y < 0 || y >= this.gridSize) return;

    const px = this.offsetX + x * this.tileSize;
    const py = this.offsetY + y * this.tileSize;
    const size = this.tileSize;
    const { ctx } = this;

    // Teal highlight with transparency
    ctx.fillStyle = 'rgba(42, 157, 143, 0.25)';
    ctx.fillRect(px, py, size, size);

    // Brighter border
    ctx.strokeStyle = 'rgba(42, 157, 143, 0.6)';
    ctx.lineWidth = 2;
    ctx.strokeRect(px + 1, py + 1, size - 2, size - 2);
  }

  private drawToolCursor(): void {
    if (!this.hoveredTile) return;

    const { x, y } = this.hoveredTile;
    if (x < 0 || x >= this.gridSize || y < 0 || y >= this.gridSize) return;

    const px = this.offsetX + x * this.tileSize;
    const py = this.offsetY + y * this.tileSize;
    const { ctx } = this;

    const icon = TOOL_ICONS[this.selectedTool] || TOOL_ICONS.trowel;

    ctx.font = `${Math.floor(this.tileSize * 0.4)}px serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.globalAlpha = 0.8;
    ctx.fillText(icon, px + this.tileSize - 2, py + this.tileSize - 2);
    ctx.globalAlpha = 1.0;

    // For charges, show 3x3 area highlight
    if (this.selectedTool === 'charges') {
      ctx.fillStyle = 'rgba(199, 91, 57, 0.15)';
      ctx.strokeStyle = 'rgba(199, 91, 57, 0.4)';
      ctx.lineWidth = 1;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx >= 0 && nx < this.gridSize && ny >= 0 && ny < this.gridSize) {
            if (dx === 0 && dy === 0) continue; // center already highlighted by hover
            const npx = this.offsetX + nx * this.tileSize;
            const npy = this.offsetY + ny * this.tileSize;
            ctx.fillRect(npx, npy, this.tileSize, this.tileSize);
            ctx.strokeRect(npx + 0.5, npy + 0.5, this.tileSize - 1, this.tileSize - 1);
          }
        }
      }
    }

    // For GPR, show scan radius hint
    if (this.selectedTool === 'gpr') {
      ctx.strokeStyle = 'rgba(42, 157, 143, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      const centerX = px + this.tileSize / 2;
      const centerY = py + this.tileSize / 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, this.tileSize * 2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  private drawDepthIndicator(tile: TileDef, px: number, py: number): void {
    const { ctx } = this;
    const totalLayers = tile.layers.length;
    const currentLayer = tile.currentLayer;
    const remaining = totalLayers - currentLayer;

    if (remaining <= 0) return;

    // Draw small dots in bottom-left corner indicating remaining depth
    const dotSize = Math.max(2, Math.floor(this.tileSize * 0.05));
    const spacing = dotSize * 2.5;
    const startX = px + 4;
    const startY = py + this.tileSize - 4;

    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    const maxDots = Math.min(remaining, 6);
    for (let i = 0; i < maxDots; i++) {
      const dotX = startX + i * spacing;
      const dotY = startY - dotSize;
      ctx.beginPath();
      ctx.arc(dotX, dotY, dotSize, 0, Math.PI * 2);
      ctx.fill();
    }

    // If more than 6 remaining, show a small number instead
    if (remaining > 6) {
      ctx.font = `${Math.max(9, Math.floor(this.tileSize * 0.2))}px monospace`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.fillText(`${remaining}`, px + 3, py + this.tileSize - 2);
    }
  }

  private drawArtifactIcon(tile: TileDef, px: number, py: number): void {
    const { ctx } = this;
    const size = this.tileSize;
    const centerX = px + size / 2;
    const centerY = py + size / 2;

    // Golden glow behind the icon
    const glowRadius = size * 0.4;
    const grad = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, glowRadius);
    grad.addColorStop(0, 'rgba(184, 134, 11, 0.5)');
    grad.addColorStop(0.6, 'rgba(184, 134, 11, 0.15)');
    grad.addColorStop(1, 'rgba(184, 134, 11, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(px, py, size, size);

    // Sparkle icon
    ctx.font = `${Math.floor(size * 0.45)}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('\u2728', centerX, centerY);
  }

  private drawArtifactHint(px: number, py: number): void {
    const { ctx } = this;
    const size = this.tileSize;
    // Subtle pulsing shimmer to hint "something is here"
    const pulse = 0.1 + 0.08 * Math.sin(this.pulsePhase * 0.6);
    ctx.fillStyle = `rgba(184, 134, 11, ${pulse})`;
    ctx.fillRect(px, py, size, size);
  }

  private drawMarkedOverlay(px: number, py: number): void {
    const { ctx } = this;
    const size = this.tileSize;

    // Teal border overlay
    ctx.strokeStyle = TEAL_ACCENT;
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(px + 2, py + 2, size - 4, size - 4);
    ctx.setLineDash([]);

    // Subtle teal fill
    ctx.fillStyle = 'rgba(42, 157, 143, 0.1)';
    ctx.fillRect(px, py, size, size);

    // Small flag icon in top-right
    ctx.font = `${Math.max(10, Math.floor(size * 0.25))}px serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillStyle = TEAL_ACCENT;
    ctx.fillText('\u2691', px + size - 3, py + 2);
  }

  private drawScannedOverlay(tile: TileDef, px: number, py: number): void {
    const { ctx } = this;
    const size = this.tileSize;

    // Light scan tint
    ctx.fillStyle = 'rgba(42, 157, 143, 0.08)';
    ctx.fillRect(px, py, size, size);

    // If artifact underneath, show pulsing indicator
    if (tile.artifactId && !tile.revealed) {
      const pulse = 0.3 + 0.3 * Math.sin(this.pulsePhase * 0.8);
      const centerX = px + size / 2;
      const centerY = py + size / 2;
      const radius = size * 0.2;

      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(184, 134, 11, ${pulse})`;
      ctx.fill();

      // Outer ring
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius + 3, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(184, 134, 11, ${pulse * 0.5})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Small scan icon in top-left
    ctx.font = `${Math.max(9, Math.floor(size * 0.2))}px serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.globalAlpha = 0.5;
    ctx.fillText('\uD83D\uDCE1', px + 2, py + 2);
    ctx.globalAlpha = 1.0;
  }

  // --- Animation system ---

  addAnimation(anim: Animation): void {
    this.animations.push(anim);
  }

  private updateAnimations(): void {
    const now = performance.now();
    this.animations = this.animations.filter(
      (a) => now - a.startTime < a.duration
    );
  }

  private drawAnimations(): void {
    for (const anim of this.animations) {
      switch (anim.type) {
        case 'dig':
          this.drawDigAnimation(anim);
          break;
        case 'discovery':
          this.drawDiscoveryAnimation(anim);
          break;
        case 'scan':
          this.drawScanAnimation(anim);
          break;
        case 'charges':
          this.drawChargesAnimation(anim);
          break;
      }
    }
  }

  private drawDigAnimation(anim: Animation): void {
    const { ctx } = this;
    const elapsed = performance.now() - anim.startTime;
    const progress = Math.min(elapsed / anim.duration, 1);

    const centerX = this.offsetX + anim.x * this.tileSize + this.tileSize / 2;
    const centerY = this.offsetY + anim.y * this.tileSize + this.tileSize / 2;

    // Generate deterministic particles based on tile position
    const particleCount = 4 + ((anim.x * 7 + anim.y * 11) % 5);
    const seed = anim.x * 31 + anim.y * 47;

    for (let i = 0; i < particleCount; i++) {
      const angle = ((seed + i * 67) % 360) * (Math.PI / 180);
      const speed = 20 + ((seed + i * 23) % 30);
      const spreadX = Math.cos(angle) * speed * progress;
      const riseY = -40 * progress - Math.sin(angle) * speed * 0.5 * progress;

      const px = centerX + spreadX;
      const py = centerY + riseY;
      const alpha = 1 - progress;
      const particleSize = (2 + ((seed + i) % 3)) * (1 - progress * 0.5);

      // Brown dust particles
      const brown = i % 2 === 0 ? '#8D6E63' : '#5D4037';
      ctx.globalAlpha = alpha;
      ctx.fillStyle = brown;
      ctx.beginPath();
      ctx.arc(px, py, particleSize, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1.0;
  }

  private drawDiscoveryAnimation(anim: Animation): void {
    const { ctx } = this;
    const elapsed = performance.now() - anim.startTime;
    const progress = Math.min(elapsed / anim.duration, 1);

    const centerX = this.offsetX + anim.x * this.tileSize + this.tileSize / 2;
    const centerY = this.offsetY + anim.y * this.tileSize + this.tileSize / 2;

    // Golden sparkles expanding outward
    const sparkleCount = 8;
    for (let i = 0; i < sparkleCount; i++) {
      const angle = (i / sparkleCount) * Math.PI * 2 + progress * 0.5;
      const radius = progress * this.tileSize * 0.8;
      const px = centerX + Math.cos(angle) * radius;
      const py = centerY + Math.sin(angle) * radius;

      // Fade in then out
      const alpha = progress < 0.3 ? progress / 0.3 : 1 - ((progress - 0.3) / 0.7);
      const sparkleSize = 2 + Math.sin(progress * Math.PI) * 3;

      ctx.globalAlpha = Math.max(0, alpha);

      // Draw a 4-point star shape
      ctx.fillStyle = AGED_GOLD;
      ctx.beginPath();
      ctx.moveTo(px, py - sparkleSize);
      ctx.lineTo(px + sparkleSize * 0.3, py);
      ctx.lineTo(px, py + sparkleSize);
      ctx.lineTo(px - sparkleSize * 0.3, py);
      ctx.closePath();
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(px - sparkleSize, py);
      ctx.lineTo(px, py + sparkleSize * 0.3);
      ctx.lineTo(px + sparkleSize, py);
      ctx.lineTo(px, py - sparkleSize * 0.3);
      ctx.closePath();
      ctx.fill();
    }

    // Central glow
    const glowAlpha = Math.sin(progress * Math.PI) * 0.6;
    const glowRadius = Math.max(0.1, this.tileSize * 0.5 * progress);
    const grad = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, glowRadius);
    grad.addColorStop(0, `rgba(184, 134, 11, ${glowAlpha})`);
    grad.addColorStop(1, 'rgba(184, 134, 11, 0)');
    ctx.globalAlpha = 1.0;
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(centerX, centerY, glowRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 1.0;
  }

  private drawScanAnimation(anim: Animation): void {
    const { ctx } = this;
    const elapsed = performance.now() - anim.startTime;
    const progress = Math.min(elapsed / anim.duration, 1);

    const centerX = this.offsetX + anim.x * this.tileSize + this.tileSize / 2;
    const centerY = this.offsetY + anim.y * this.tileSize + this.tileSize / 2;

    // Expanding teal circle
    const maxRadius = this.tileSize * 2;
    const radius = progress * maxRadius;
    const alpha = 0.5 * (1 - progress);

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(42, 157, 143, ${alpha})`;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Inner fill that fades
    ctx.fillStyle = `rgba(42, 157, 143, ${alpha * 0.3})`;
    ctx.fill();

    // Second ring trailing behind
    if (progress > 0.2) {
      const trailProgress = (progress - 0.2) / 0.8;
      const trailRadius = trailProgress * maxRadius;
      const trailAlpha = 0.3 * (1 - trailProgress);
      ctx.beginPath();
      ctx.arc(centerX, centerY, trailRadius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(42, 157, 143, ${trailAlpha})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  private drawChargesAnimation(anim: Animation): void {
    const { ctx } = this;
    const elapsed = performance.now() - anim.startTime;
    const progress = Math.min(elapsed / anim.duration, 1);

    const centerX = this.offsetX + anim.x * this.tileSize + this.tileSize / 2;
    const centerY = this.offsetY + anim.y * this.tileSize + this.tileSize / 2;

    // Orange/red expanding flash
    const maxRadius = this.tileSize * 1.8;
    const radius = progress * maxRadius;

    // Bright core
    if (progress < 0.5) {
      const coreAlpha = 1 - progress * 2;
      const coreRadius = Math.max(0.1, radius * 0.4);
      const coreGrad = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, coreRadius
      );
      coreGrad.addColorStop(0, `rgba(255, 255, 200, ${coreAlpha})`);
      coreGrad.addColorStop(0.5, `rgba(255, 150, 50, ${coreAlpha * 0.7})`);
      coreGrad.addColorStop(1, `rgba(199, 91, 57, 0)`);
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // Outer shockwave ring
    const ringAlpha = 0.7 * (1 - progress);
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(199, 91, 57, ${ringAlpha})`;
    ctx.lineWidth = 3 * (1 - progress);
    ctx.stroke();

    // Debris particles flying outward
    const debrisCount = 6;
    const seed = anim.x * 19 + anim.y * 37;
    for (let i = 0; i < debrisCount; i++) {
      const angle = ((seed + i * 60) % 360) * (Math.PI / 180);
      const dist = radius * (0.5 + ((seed + i * 13) % 50) / 100);
      const dx = centerX + Math.cos(angle) * dist;
      const dy = centerY + Math.sin(angle) * dist;
      const debrisAlpha = Math.max(0, 1 - progress * 1.2);
      const debrisSize = 2 + ((seed + i) % 3);

      ctx.globalAlpha = debrisAlpha;
      ctx.fillStyle = i % 2 === 0 ? TERRACOTTA : '#A0522D';
      ctx.fillRect(dx - debrisSize / 2, dy - debrisSize / 2, debrisSize, debrisSize);
    }
    ctx.globalAlpha = 1.0;
  }

  // --- Coordinate conversion ---

  screenToGrid(screenX: number, screenY: number): { x: number; y: number } | null {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    const canvasX = (screenX - rect.left) * scaleX;
    const canvasY = (screenY - rect.top) * scaleY;

    const gx = Math.floor((canvasX - this.offsetX) / this.tileSize);
    const gy = Math.floor((canvasY - this.offsetY) / this.tileSize);

    if (gx >= 0 && gx < this.gridSize && gy >= 0 && gy < this.gridSize) {
      return { x: gx, y: gy };
    }
    return null;
  }

  // --- Input handling ---

  setupInputHandlers(onClick: (gx: number, gy: number) => void): void {
    const canvas = this.canvas;

    // Mouse move for hover
    canvas.addEventListener('mousemove', (e: MouseEvent) => {
      if (this.isDragging) {
        const dx = e.clientX - this.dragStartX;
        const dy = e.clientY - this.dragStartY;
        this.scrollX = this.scrollStartX + dx;
        this.scrollY = this.scrollStartY + dy;
        this.clampScroll();
        return;
      }

      const pos = this.screenToGrid(e.clientX, e.clientY);
      if (pos) {
        this.hoveredTile = pos;
      } else {
        this.hoveredTile = null;
      }
    });

    // Mouse leave
    canvas.addEventListener('mouseleave', () => {
      this.hoveredTile = null;
      this.isDragging = false;
    });

    // Mouse down for drag
    canvas.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button === 1 || e.button === 2) {
        // Middle or right click starts drag
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.scrollStartX = this.scrollX;
        this.scrollStartY = this.scrollY;
        e.preventDefault();
      }
    });

    canvas.addEventListener('mouseup', (e: MouseEvent) => {
      if (this.isDragging) {
        this.isDragging = false;
        return;
      }
    });

    // Click for tile interaction
    canvas.addEventListener('click', (e: MouseEvent) => {
      if (this.isDragging) return;

      const pos = this.screenToGrid(e.clientX, e.clientY);
      if (pos) {
        onClick(pos.x, pos.y);
      }
    });

    // Prevent context menu on right-click (used for panning)
    canvas.addEventListener('contextmenu', (e: Event) => {
      e.preventDefault();
    });

    // Wheel for scrolling
    canvas.addEventListener('wheel', (e: WheelEvent) => {
      this.scrollX -= e.deltaX;
      this.scrollY -= e.deltaY;
      this.clampScroll();
      e.preventDefault();
    }, { passive: false });

    // Touch support
    let touchStartX = 0;
    let touchStartY = 0;
    let touchScrollStartX = 0;
    let touchScrollStartY = 0;
    let isTouchDragging = false;
    let touchStartTime = 0;
    let touchMoved = false;

    canvas.addEventListener('touchstart', (e: TouchEvent) => {
      if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchScrollStartX = this.scrollX;
        touchScrollStartY = this.scrollY;
        touchStartTime = performance.now();
        touchMoved = false;
        isTouchDragging = false;
      } else if (e.touches.length === 2) {
        // Two-finger drag for scroll
        isTouchDragging = true;
        touchStartX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        touchStartY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        touchScrollStartX = this.scrollX;
        touchScrollStartY = this.scrollY;
      }
      e.preventDefault();
    }, { passive: false });

    canvas.addEventListener('touchmove', (e: TouchEvent) => {
      if (e.touches.length === 1 && !isTouchDragging) {
        const dx = e.touches[0].clientX - touchStartX;
        const dy = e.touches[0].clientY - touchStartY;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          touchMoved = true;
          isTouchDragging = true;
        }
        if (isTouchDragging) {
          this.scrollX = touchScrollStartX + dx;
          this.scrollY = touchScrollStartY + dy;
          this.clampScroll();
        }
      } else if (e.touches.length === 2) {
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        this.scrollX = touchScrollStartX + (midX - touchStartX);
        this.scrollY = touchScrollStartY + (midY - touchStartY);
        this.clampScroll();
      }
      e.preventDefault();
    }, { passive: false });

    canvas.addEventListener('touchend', (e: TouchEvent) => {
      if (!touchMoved && !isTouchDragging && e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        const elapsed = performance.now() - touchStartTime;
        // Tap = click, only if short and no movement
        if (elapsed < 300) {
          const pos = this.screenToGrid(touch.clientX, touch.clientY);
          if (pos) {
            onClick(pos.x, pos.y);
          }
        }
      }
      isTouchDragging = false;
      touchMoved = false;
      e.preventDefault();
    }, { passive: false });
  }

  private clampScroll(): void {
    const totalGridW = this.gridSize * this.tileSize;
    const totalGridH = this.gridSize * this.tileSize;
    const maxScrollX = Math.max(0, (totalGridW - this.canvas.width) / 2 + this.tileSize * 2);
    const maxScrollY = Math.max(0, (totalGridH - this.canvas.height) / 2 + this.tileSize * 2);
    this.scrollX = Math.max(-maxScrollX, Math.min(maxScrollX, this.scrollX));
    this.scrollY = Math.max(-maxScrollY, Math.min(maxScrollY, this.scrollY));
  }

  // --- Hover & tool state ---

  setHoveredTile(x: number, y: number): void {
    this.hoveredTile = { x, y };
  }

  clearHover(): void {
    this.hoveredTile = null;
  }

  setSelectedTool(tool: string): void {
    this.selectedTool = tool;
  }

  // --- Resize ---

  resize(): void {
    const parent = this.canvas.parentElement;
    if (parent) {
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      // Skip resize if parent has collapsed (zero dimensions)
      if (w === 0 || h === 0) return;
      this.canvas.width = w;
      this.canvas.height = h;
    } else {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    }

    // Calculate tile size to fit the grid within the canvas
    if (this.gridSize > 0) {
      const maxTileW = Math.floor(this.canvas.width / this.gridSize);
      const maxTileH = Math.floor(this.canvas.height / this.gridSize);
      this.tileSize = Math.max(40, Math.min(maxTileW, maxTileH, 64));
    }

    // Clamp scroll instead of resetting — preserve user's scroll position
    this.clampScroll();
  }

  /** Reset scroll to origin. Call explicitly when loading a new site. */
  resetScroll(): void {
    this.scrollX = 0;
    this.scrollY = 0;
  }

  // --- Color utilities ---

  private getMaterialColor(material: MaterialType): string {
    return MATERIAL_COLORS[material] || '#808080';
  }

  private darkenColor(hex: string, amount: number): string {
    // Parse hex color
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // Darken by reducing each channel
    const factor = Math.max(0, 1 - amount);
    const dr = Math.round(r * factor);
    const dg = Math.round(g * factor);
    const db = Math.round(b * factor);

    return (
      '#' +
      dr.toString(16).padStart(2, '0') +
      dg.toString(16).padStart(2, '0') +
      db.toString(16).padStart(2, '0')
    );
  }

  // --- Completion overlay ---

  drawCompletionOverlay(percent: number): void {
    const { ctx, canvas } = this;

    // Semi-transparent dark overlay
    ctx.fillStyle = 'rgba(26, 26, 46, 0.7)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // "Site Complete" text
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Title
    ctx.font = 'bold 32px serif';
    ctx.fillStyle = AGED_GOLD;
    ctx.fillText('Site Excavation Complete', centerX, centerY - 50);

    // Percentage
    ctx.font = 'bold 48px monospace';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(`${Math.round(percent)}%`, centerX, centerY + 10);

    // Subtitle
    ctx.font = '16px sans-serif';
    ctx.fillStyle = '#C0C0C0';
    ctx.fillText('of artifacts recovered', centerX, centerY + 50);

    // Progress bar
    const barWidth = 240;
    const barHeight = 12;
    const barX = centerX - barWidth / 2;
    const barY = centerY + 75;

    // Bar background
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barWidth, barHeight, 6);
    ctx.fill();

    // Bar fill
    const fillWidth = barWidth * (percent / 100);
    if (fillWidth > 0) {
      const barGrad = ctx.createLinearGradient(barX, barY, barX + fillWidth, barY);
      barGrad.addColorStop(0, TEAL_ACCENT);
      barGrad.addColorStop(1, AGED_GOLD);
      ctx.fillStyle = barGrad;
      ctx.beginPath();
      ctx.roundRect(barX, barY, fillWidth, barHeight, 6);
      ctx.fill();
    }
  }
}
